import tokenService from './services/tokenService';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const OAuthLoginSuccess = ({ isAuthenticated, authToken, user }) => {
  const navigate = useNavigate();
  const [animationStage, setAnimationStage] = useState('initial');
  const [showContent, setShowContent] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [authVerified, setAuthVerified] = useState(false);

  useEffect(() => {
    // Verify authentication status before starting countdown
    const verifyAuth = async () => {
      // Phase 2: the auth cookie is httpOnly so JS can't read it. We trust
      // the parent component's `isAuthenticated` + `user` props (which were
      // populated by the cookie-based session probe in AppRouter).
      if (isAuthenticated && user) {
        console.log('✅ OAuth Success: Full authentication verified');
        console.log('   - isAuthenticated prop:', isAuthenticated);
        console.log('   - User data:', !!user);
        setAuthVerified(true);
      } else {
        console.log('⏳ OAuth Success: Waiting for authentication to complete...');
        console.log('   - isAuthenticated prop:', isAuthenticated);
        console.log('   - User data:', !!user);
        // Keep waiting — AppRouter's session probe will populate these props
        // shortly. If it never does, the redirect-on-timeout below catches it.
      }
    };

    verifyAuth();
  }, [navigate, isAuthenticated, user]);

  useEffect(() => {
    // Start the animation sequence
    const timer1 = setTimeout(() => setAnimationStage('opening'), 500);
    const timer2 = setTimeout(() => setAnimationStage('opened'), 2000);
    const timer3 = setTimeout(() => setShowContent(true), 2500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  useEffect(() => {
    // Only start countdown after auth is verified
    if (!authVerified) return;

    console.log('🔑 OAuth Success: Authentication verified, starting redirect countdown...');
    
    const countdownInterval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          console.log('🏠 OAuth Success: Redirecting to library');
          // Double-check authentication before redirecting
          if (isAuthenticated && user && authToken) {
            navigate('/library');
          } else {
            console.error('❌ OAuth Success: Authentication lost during countdown, redirecting to auth');
            navigate('/auth');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [navigate, authVerified, isAuthenticated, user, authToken]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #2C3A4C 0%, #1A202C 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Magical particles in background */}
      <div className="particle-container">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              background: '#D7B377',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: Math.random() * 0.5 + 0.3
            }}
          />
        ))}
      </div>

      {/* Main Book Animation */}
      <div style={{
        position: 'relative',
        perspective: '1000px',
        width: '400px',
        height: '600px'
      }}>
        {/* Book Container */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transform: animationStage === 'initial' ? 'rotateY(-180deg)' : 'rotateY(0deg)',
          transition: 'transform 1.5s ease-in-out'
        }}>
          {/* Book Back Cover */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
            borderRadius: '0 10px 10px 0',
            border: '3px solid #D7B377',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
          }}>
            {/* Ornamental design on back */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              border: '2px solid #D7B377',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                fontSize: '3rem',
                color: '#D7B377',
                fontFamily: 'serif',
                textShadow: '0 0 20px rgba(215, 179, 119, 0.5)'
              }}>
                VS
              </div>
            </div>
          </div>

          {/* Book Front (Opening) */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transformOrigin: 'left center',
            transform: animationStage === 'opened' ? 'rotateY(-170deg)' : 'rotateY(0deg)',
            transition: 'transform 1.5s ease-in-out',
            transformStyle: 'preserve-3d'
          }}>
            {/* Front Cover */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
              borderRadius: '0 10px 10px 0',
              border: '3px solid #D7B377',
              backfaceVisibility: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}>
              {/* Book spine */}
              <div style={{
                position: 'absolute',
                left: '-20px',
                top: '-3px',
                width: '20px',
                height: 'calc(100% + 6px)',
                background: '#654321',
                borderLeft: '2px solid #D7B377',
                borderTop: '3px solid #D7B377',
                borderBottom: '3px solid #D7B377'
              }} />

              {/* Front cover design */}
              <div style={{
                position: 'absolute',
                top: '30px',
                left: '30px',
                right: '30px',
                bottom: '30px',
                border: '2px solid #D7B377',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                <h1 style={{
                  fontSize: '2.5rem',
                  color: '#D7B377',
                  fontFamily: 'serif',
                  margin: 0,
                  textAlign: 'center',
                  textShadow: '0 0 20px rgba(215, 179, 119, 0.5)'
                }}>
                  VoxSmyth
                </h1>
                <div style={{
                  width: '60px',
                  height: '2px',
                  background: '#D7B377',
                  margin: '20px 0',
                  opacity: 0.6
                }} />
                <p style={{
                  color: '#F7F3E9',
                  fontSize: '1rem',
                  textAlign: 'center',
                  margin: 0,
                  opacity: 0.8
                }}>
                  Your Literary Journey
                </p>
              </div>
            </div>

            {/* Inside of front cover */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: '#F7F3E9',
              borderRadius: '0 10px 10px 0',
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              boxShadow: 'inset 0 0 20px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                textAlign: 'center',
                opacity: showContent ? 1 : 0,
                transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.8s ease-out'
              }}>
                <h2 style={{
                  fontSize: '2rem',
                  color: '#2C3A4C',
                  fontFamily: 'serif',
                  marginBottom: '20px'
                }}>
                  Welcome to Your Library
                </h2>
                <p style={{
                  fontSize: '1.2rem',
                  color: '#4A5568',
                  marginBottom: '30px',
                  lineHeight: 1.6
                }}>
                  Your magical journey begins now.<br />
                  Thousands of stories await your voice.
                </p>

                {/* Decorative element */}
                <div style={{
                  width: '80px',
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, #D7B377, transparent)',
                  margin: '0 auto 30px'
                }} />

                {/* Countdown */}
                <p style={{
                  fontSize: '1rem',
                  color: '#718096',
                  marginBottom: '20px'
                }}>
                  Entering the Library in {redirectCountdown}...
                </p>

                {/* Manual continue button */}
                <button
                  onClick={() => navigate('/library')}
                  style={{
                    padding: '12px 30px',
                    background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                    border: 'none',
                    borderRadius: '25px',
                    color: '#2C3A4C',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 5px 15px rgba(215, 179, 119, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 7px 20px rgba(215, 179, 119, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 5px 15px rgba(215, 179, 119, 0.3)';
                  }}
                >
                  Enter Now
                </button>
              </div>
            </div>
          </div>

          {/* Book Pages (visible when opening) */}
          {animationStage !== 'initial' && (
            <div style={{
              position: 'absolute',
              width: 'calc(100% - 40px)',
              height: 'calc(100% - 40px)',
              top: '20px',
              left: '20px',
              background: 'linear-gradient(90deg, #F7F3E9 0%, #FFF 50%, #F7F3E9 100%)',
              borderRadius: '0 5px 5px 0',
              boxShadow: 'inset 0 0 30px rgba(0,0,0,0.1)',
              opacity: animationStage === 'opened' ? 1 : 0,
              transition: 'opacity 1s ease-in-out'
            }}>
              {/* Page lines */}
              {[...Array(15)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: '10%',
                    right: '10%',
                    top: `${20 + i * 30}px`,
                    height: '1px',
                    background: 'rgba(0,0,0,0.05)'
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Magical glow effect */}
        {animationStage === 'opened' && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '150%',
            height: '150%',
            background: 'radial-gradient(circle, rgba(215,179,119,0.3) 0%, transparent 70%)',
            animation: 'pulse 2s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
        )}
      </div>

      {/* Corner sparkles */}
      {showContent && (
        <>
          <Sparkle
            style={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              animation: 'sparkle-fade 2s ease-in-out infinite'
            }}
            size={24}
          />
          <Sparkle
            style={{
              position: 'absolute',
              top: '15%',
              right: '15%',
              animation: 'sparkle-fade 2s ease-in-out 0.5s infinite'
            }}
            size={16}
          />
          <Sparkle
            style={{
              position: 'absolute',
              bottom: '20%',
              left: '20%',
              animation: 'sparkle-fade 2s ease-in-out 1s infinite'
            }}
            size={20}
          />
          <Sparkle
            style={{
              position: 'absolute',
              bottom: '10%',
              right: '10%',
              animation: 'sparkle-fade 2s ease-in-out 1.5s infinite'
            }}
            size={18}
          />
        </>
      )}

      {/* Add keyframe animations to document head */}
      <style>
        {`
        @keyframes float {
          0% {
            transform: translateY(0) translateX(0);
          }
          33% {
            transform: translateY(-30px) translateX(10px);
          }
          66% {
            transform: translateY(20px) translateX(-10px);
          }
          100% {
            transform: translateY(0) translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.5;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        @keyframes sparkle-fade {
          0%, 100% {
            opacity: 0;
            transform: scale(0.8) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1) rotate(180deg);
          }
        }
        `}
      </style>
    </div>
  );
};

// Sparkle component
const Sparkle = ({ size = 20, style = {} }) => (
  <div style={style}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z"
        fill="#D7B377"
        fillOpacity="0.8"
      />
    </svg>
  </div>
);

export default OAuthLoginSuccess;